/**
 * ISO 22081:2021 - General geometrical specifications and general size specifications.
 * Replaces ISO 2768-2:1989 for geometrical tolerancing.
 */

export const ISO22081_DROPDOWN_OPTIONS = [ 'ISO 22081' ] as const;

export const ISO22081_DEFAULT = 'ISO 22081';

/** Parsed general tolerance values for linear / shape / angular / radius & chamfer (the ± value only, one number as string). */
export interface Iso22081ParsedBands {
    linear: string;
    shape: string;
    angular: string;
    radiusChamfer: string;
    mentions22081: boolean;
}

function normalizeTolNum(s: string): string {
    const t = s.trim().replace(',', '.');
    if (!t) return '';
    return t;
}

/**
 * True if the text references standard ISO 22081 (e.g. "ISO 22081", "ISO22081", or a standalone 22081 token).
 */
export function textReferencesIso22081(text: string): boolean {
    if (!text || !String(text).trim()) return false;
    const t = String(text);
    if (/\biso\s*22081\b/i.test(t) || /\biso22081\b/i.test(t)) return true;
    // Standalone 22081 (not part of a longer number)
    return /(?:^|[^\d.])22081(?!\d)/.test(t);
}

function takeToleranceForLine(line: string): string | null {
    const m = line.match(/±\s*([0-9]+[.,]?[0-9]*)/);
    if (m) return normalizeTolNum(m[1]);
    const m2 = line.match(
        /\+\s*([0-9]+[.,]?[0-9]*)\s*\/\s*-\s*([0-9]+[.,]?[0-9]*)/
    );
    if (m2) return normalizeTolNum(m2[1]);
    return null;
}

/**
 * Parse title-block style text: labels + ± next to "ISO 22081" / 22081, or up to four ± values in document order
 * (linear, radius & chamfer, shape, angular — same as editor fields order).
 */
export function parseIso22081ToleranceBandsFromTitleBlockText(
    blockText: string
): Iso22081ParsedBands | null {
    const full = (blockText || '').replace(/\r/g, '\n');
    if (!textReferencesIso22081(full)) return null;

    const result: Iso22081ParsedBands = {
        linear: '',
        shape: '',
        angular: '',
        radiusChamfer: '',
        mentions22081: true
    };

    // Keywords: EN / common EU languages / RU / ET — avoid matching dim numbers
    const LINEAR_RE =
        /(\blinear\b|lineaarsed?|lineaar|lineaarse|toleran.*(linear|line)|lintoleran|l\s*\.|lintol|лини|лине)/i;
    const SHAPE_RE =
        /(\bshape\b|kuju|toleran.*(shape|kuju|form|profile)|pinnavorm|pinn|форм(ы|а|е)?|profiil)/i;
    const ANG_RE =
        /(\bangular\b|nurg|nurga|nurgatoleran|winkel|winkeltoler|angle|угл(ов(ые|ой|ы|a)?)?|kąt|kątów)/i;
    const RAD_RE =
        /(\bradius\b|\bchamfer\b|raadius|faas|fask|R\s*[,;]|(R|C)\s*\/\s*C|R\s*\+\s*C|радиус|фаск|konus|koonus)/i;

    const lines = full.split(/\n/).map((l) => l.trim()).filter(Boolean);
    for (const line of lines) {
        const v = takeToleranceForLine(line);
        if (!v) continue;
        if (LINEAR_RE.test(line) && !result.linear) result.linear = v;
        else if (SHAPE_RE.test(line) && !result.shape) result.shape = v;
        else if (ANG_RE.test(line) && !result.angular) result.angular = v;
        else if (RAD_RE.test(line) && !result.radiusChamfer) result.radiusChamfer = v;
    }

    const noneParsed =
        !result.linear && !result.shape && !result.angular && !result.radiusChamfer;
    if (noneParsed) {
        const nums: string[] = [];
        const reTol = /±\s*([0-9]+[.,][0-9]+|[0-9]+)/g;
        for (const m of full.matchAll(reTol)) {
            nums.push(normalizeTolNum(m[1]));
        }
        if (nums.length > 0) {
            if (nums.length >= 4) {
                result.linear = nums[0];
                result.radiusChamfer = nums[1];
                result.shape = nums[2];
                result.angular = nums[3];
            } else {
                for (let i = 0; i < nums.length; i++) {
                    if (i === 0) result.linear = nums[0];
                    else if (i === 1) result.radiusChamfer = nums[1];
                    else if (i === 2) result.shape = nums[2];
                    else if (i === 3) result.angular = nums[3];
                }
            }
        }
    }

    return result;
}
